import { setProp, getProp, removeProp } from 'object-path-operator'

export interface AsyncProxyHandler<T> {
  get?(target: T, path: PropertyKey[]/*, receiver: any*/): Promise<any>
  apply?(target: T, path: PropertyKey[], thisArg: any, argArray?: any): Promise<any>
  set?(target: T, path: PropertyKey[], value: any/*, receiver: any*/): Promise<void>
  construct?(target: T, path: PropertyKey[], argArray: any, newTarget?: any): Promise<object>
  getPrototypeOf?(target: T, path: PropertyKey[]): Promise<object | null>
  setPrototypeOf?(target: T, path: PropertyKey[], v: any): Promise<void>
  deleteProperty?(target: T, path: PropertyKey[]): Promise<void>
  defineProperty?(target: T, path: PropertyKey[], p: PropertyKey, attributes: PropertyDescriptor): Promise<void>
}

interface StrongAsyncProxyHandler<T> extends AsyncProxyHandler<T> {
  get(target: T, path: PropertyKey[]/*, receiver: any*/): Promise<any>
  apply(target: T, path: PropertyKey[], thisArg: any, argArray?: any): Promise<any>
  set(target: T, path: PropertyKey[], value: any/*, receiver: any*/): Promise<void>
  construct(target: T, path: PropertyKey[], argArray: any, newTarget?: any): Promise<object>
  getPrototypeOf(target: T, path: PropertyKey[]): Promise<object | null>
  setPrototypeOf(target: T, path: PropertyKey[], v: any): Promise<void>
  deleteProperty(target: T, path: PropertyKey[]): Promise<void>
  defineProperty(target: T, path: PropertyKey[], p: PropertyKey, attributes: PropertyDescriptor): Promise<void>
}

/**
 * Create a Proxy to asynchronous operate object.
 *
 * @param {Object} [target = {}] - Target object
 * @param {Object} [handlers = {}] - Async Proxy handlers
 * @param {function(target: Object, path: string[]): Promise<any>} handlers.get - "get" handler
 * @param {function(target: Object, path: string[], caller: Object, args: any[]): Promsie<any>} handlers.apply - "apply" handler
 * @param {function(target: Object, path: string[], value: any): void} handlers.set - "set" handler
 * @param {function(target: Object, path: string[]): void} handlers.deleteProperty - "deleteProperty" handler
 * @param {function(target: Object, path: string[], args: any[]): Promise<any>} handlers.construct - "construct" handler
 * @param {function(target: Object, path: string[], prototype: Object): void} handlers.setPrototypeOf - "setPrototypeOf" handler
 * @param {function(target: Object, path: string[]): Promise<any>} handlers.getPrototypeOf - "getPrototypeOf" handler
 * @param {function(target: Object, path: string[], prop: string, descriptor: Object): void} handlers.defineProperty - "defineProperty" handler
 * @return {Proxy} Async Proxy
 */
export function createAsyncProxy<T extends object>(target: T = Object.create(null), asyncProxyHandlers: AsyncProxyHandler<T> = Object.create(null)) {
  class CallableObject extends Function {}

  const handlers: StrongAsyncProxyHandler<T> = {
    async get(target: T, path: PropertyKey[]): Promise<any> {
      return getProp(target, path)
    }
  , async apply(target: T, path: PropertyKey[], caller: any, args: any[]): Promise<any> {
      return Reflect.apply(getProp(target, path) as Function, caller, args)
    }
  , async set(target: T, path: PropertyKey[], value: any): Promise<void> {
      setProp(target, path, value) // The return value will be ignored
    }
  , async deleteProperty(target: T, path: PropertyKey[]): Promise<void> {
      removeProp(target, path) // The return value will be ignored
    }
  , async construct(target: T, path: PropertyKey[], args: any): Promise<any> {
      return Reflect.construct(getProp(target, path) as Function, args)
    }
  , async setPrototypeOf(target: T, path: PropertyKey[], prototype: any): Promise<void> {
      Reflect.setPrototypeOf(getProp(target, path) as object, prototype) // The return value will be ignored
    }
  , async getPrototypeOf(target: T, path: PropertyKey[]): Promise<object | null> {
      return Reflect.getPrototypeOf(getProp(target, path) as object)
    }
  , async defineProperty(target: T, path: PropertyKey[], prop: PropertyKey, descriptor: PropertyDescriptor): Promise<void> {
      Reflect.defineProperty(getProp(target, path) as object, prop, descriptor) // The return value will be ignored
    }
  , ...asyncProxyHandlers
  }

  function wrapper(path: PropertyKey[] = []): any {
    return new Proxy(new CallableObject(), {
      get(_, prop) {
        switch (prop) {
          case 'then':
          case 'catch':
          case 'finally':
            const finalPromise = (async () => await handlers.get(target, path))()
            return finalPromise[prop].bind(finalPromise)
          default:
            return wrapper([...path, prop])
        }
      }
    , apply(_, caller, args) {
        return handlers.apply(target, path, caller, args)
      }
    , set(_, prop, value) {
        handlers.set(target, [...path, prop], value)
        return true
      }
    , deleteProperty(_, prop) {
        handlers.deleteProperty(target, [...path, prop])
        return true
      }
    , construct(_, args) {
        return handlers.construct(target, path, args)
      }
    , setPrototypeOf(_, prototype) {
        handlers.setPrototypeOf(target, path, prototype)
        return true
      }
    , getPrototypeOf(_) {
        return handlers.getPrototypeOf(target, path)
      }
    , defineProperty(_, prop, descriptor) {
        handlers.defineProperty(target, path, prop, descriptor)
        return true
      }
    , has(_, prop) {
        throw Error('Async proxy does not support "has" handler.')
      }
    , getOwnPropertyDescriptor(_, prop) {
        throw Error('Async proxy does not support "getOwnPropertyDescriptor" handler.')
      }
    , ownKeys(_) {
        throw Error('Async proxy does not support "ownKeys" handler.')
      }
    , preventExtensions(_) {
        throw Error('Async proxy does not support "preventExtensions" handler.')
      }
    , isExtensible(_) {
        throw Error('Async proxy does not support "isExtensible" handler.')
      }
    })
  }

  return wrapper()
}

export default createAsyncProxy
